// src/components/fix-actions/action-item-detail-panel.tsx
import { useRef, useState, type MouseEvent } from "react";
import { Button } from "../ui/button";
import {
  Eye,
  Target,
  Lightbulb,
  Sparkles,
  MessageSquare,
  ChevronRight,
  CheckCircle2,
  Clock,
  FileText,
  ExternalLink,
  Radar,
  Brain,
} from "lucide-react";
import { type FixAction } from "../../services/fix-actions/fix-actions-service";
import { artifactTracker } from "../../services/fix-actions/fix-actions-service";
import { InsightActionButton } from "../insight-action-button";
import { DefinitionPopover } from "../definition-popover";
import { artifactDefinitionsService } from "../../services/fix-actions/artifactDefinitionsService";

interface ActionItemDetailPanelProps {
  action: FixAction;
  onExplainWithAI: (topic: string) => void;
  onGenerateAsset: (
    actionId: number,
    actionTitle: string,
    actionTheme: string,
    artifactTitle: string,
    artifactType: string,
    artifactDescription: string,
    artifactIndex: number,
    totalArtifacts?: number
  ) => void;
  onNavigateToStudio?: (artifactId: string) => void;
}

function getSourceBadge(sourceModule?: string) {
  const src = String(sourceModule || "").toLowerCase();
  if (src === "antenna") {
    return {
      label: "Brand Monitoring",
      color: "var(--alpha-teal)",
      bg: "var(--alpha-teal-10)",
      border: "var(--alpha-teal-20)",
      Icon: Radar,
    };
  }
  if (src === "cognition") {
    return {
      label: "AI Reasoning Path",
      color: "var(--spectral-violet)",
      bg: "var(--spectral-violet-10)",
      border: "var(--spectral-violet-20)",
      Icon: Brain,
    };
  }
  return null;
}

const getDeploymentColor = (deployment: string) => {
  switch (deployment) {
    case "Owned site":
      return "var(--alpha-teal)";
    case "Third-party blogs":
    case "Review Platforms":
      return "var(--signal-blue)";
    case "Forums":
      return "var(--infrared-coral)";
    case "Structured Data":
      return "var(--muted-foreground)";
    default:
      return "var(--muted-foreground)";
  }
};

const getEffortColor = (effort: string): string => {
  switch (effort) {
    case "Low":
      return "var(--effort-low)";
    case "Medium":
      return "var(--effort-medium)";
    case "High":
      return "var(--effort-high)";
    default:
      return "var(--muted-foreground)";
  }
};

const getImpactColor = (impact: string): string => {
  switch (impact) {
    case "Low":
      return "var(--impact-low)";
    case "Medium":
      return "var(--impact-medium)";
    case "High":
      return "var(--impact-high)";
    default:
      return "var(--muted-foreground)";
  }
};

function isNonEmptyString(v: unknown): v is string {
  return typeof v === "string" && v.trim().length > 0;
}

function getArtifactStatusIcon(status: string) {
  const s = status.toLowerCase();
  if (s === "ready" || s === "published") {
    return <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "var(--alpha-teal)" }} />;
  }
  if (s === "in review" || s === "in_review") {
    return <Clock className="w-3.5 h-3.5" style={{ color: "var(--warning-text)" }} />;
  }
  if (s === "draft") {
    return <FileText className="w-3.5 h-3.5" style={{ color: "var(--muted-foreground)" }} />;
  }
  return <Sparkles className="w-3.5 h-3.5" style={{ color: "var(--signal-blue)" }} />;
}

function getArtifactStatusColors(status: string) {
  const s = status.toLowerCase();
  if (s === "published") {
    return { bg: "var(--alpha-teal-10)", text: "var(--alpha-teal)", border: "var(--alpha-teal-20)" };
  }
  if (s === "ready") {
    return { bg: "var(--success-bg)", text: "var(--success-text)", border: "var(--success-border)" };
  }
  if (s === "in review" || s === "in_review") {
    return { bg: "var(--warning-bg)", text: "var(--warning-text)", border: "var(--warning-border)" };
  }
  return { bg: "var(--muted)", text: "var(--muted-foreground)", border: "var(--border)" };
}

// ── Shared spacing tokens ──────────────────────────────────
// Match the design system's tightened --gap-* variables. Using CSS vars so a
// future density tweak flows through automatically.
const SECTION_H_PAD = "var(--gap-md)";   // 14px — side padding for sections
const SECTION_V_GAP = "var(--gap-md)";   // 14px — vertical gap between sections
const CONTENT_BOX_PAD = "10px 12px";     // inner padding for observation/goal boxes
const SECTION_HEADING_MB = "var(--gap-xs)"; // 6px between section heading and its content

export function ActionItemDetailPanel({
  action,
  onExplainWithAI,
  onGenerateAsset,
  onNavigateToStudio,
}: ActionItemDetailPanelProps) {
  const [expandedArtifacts, setExpandedArtifacts] = useState<Set<string>>(new Set());

  const [activePopover, setActivePopover] = useState<{
    type: "artifact" | "deployment";
    id: string;
    anchorId: string;
  } | null>(null);

  const [popoverContent, setPopoverContent] = useState<{
    title: string;
    definition: string;
    items: Array<{ label?: string; text: string }>;
    proTip?: string;
  } | null>(null);

  const anchorRefs = useRef<Map<string, HTMLElement>>(new Map());

  const toggleArtifact = (artifactId: string) => {
    setExpandedArtifacts((prev) => {
      const next = new Set(prev);
      next.has(artifactId) ? next.delete(artifactId) : next.add(artifactId);
      return next;
    });
  };

  const handleArtifactTypeClick = async (
    e: MouseEvent<HTMLButtonElement>,
    artifactType: string,
    anchorId: string
  ) => {
    e.stopPropagation();
    if (activePopover?.id === artifactType && activePopover?.type === "artifact") {
      setActivePopover(null);
      return;
    }
    const definition = await artifactDefinitionsService.getArtifactTypeDefinition(artifactType);
    if (!definition) return;
    setPopoverContent({
      title: definition.type,
      definition: definition.aiReasoning,
      items: (definition.examples || []).map((ex: string) => ({ text: ex })),
      proTip:
        "AI models weight comparison and decision content heavily when users ask about alternatives.",
    });
    setActivePopover({ type: "artifact", id: artifactType, anchorId });
  };

  const handleDeploymentClick = async (
    e: MouseEvent<HTMLButtonElement>,
    channel: string,
    anchorId: string
  ) => {
    e.stopPropagation();
    if (activePopover?.id === channel && activePopover?.type === "deployment") {
      setActivePopover(null);
      return;
    }
    const definition = await artifactDefinitionsService.getDeploymentChannelDefinition(channel);
    if (!definition) return;
    setPopoverContent({
      title: definition.channel,
      definition: definition.definition,
      items: (definition.suggestions || []).map((s: string) => ({ text: s })),
      proTip: definition.bestPractices,
    });
    setActivePopover({ type: "deployment", id: channel, anchorId });
  };

  const popoverAnchorEl =
    activePopover ? anchorRefs.current.get(activePopover.anchorId) : undefined;

  const insightAccent =
    action.priority === "HIGH"
      ? "var(--infrared-coral)"
      : action.priority === "MEDIUM"
      ? "var(--signal-blue)"
      : "var(--muted-foreground)";

  const sourceBadge = getSourceBadge(action.sourceModule);
  const totalArtifacts = action.artifacts?.length ?? 0;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* ── Header — tightened padding ───────────────────────── */}
      <div
        className="border-b"
        style={{
          borderColor: "var(--border)",
          padding: `var(--gap-sm) ${SECTION_H_PAD}`, // 10px 14px
        }}
      >
        <div
          className="flex items-center flex-wrap"
          style={{ gap: 6, marginBottom: 6 }}
        >
          <div
            className="w-2 h-2 rounded-full shrink-0"
            style={{
              backgroundColor:
                action.priority === "HIGH"
                  ? "var(--infrared-coral)"
                  : action.priority === "MEDIUM"
                  ? "var(--signal-blue)"
                  : "var(--muted-foreground)",
            }}
          />

          {sourceBadge && (
            <span
              className="inline-flex items-center gap-1 rounded"
              style={{
                padding: "2px 6px",
                backgroundColor: sourceBadge.bg,
                color: sourceBadge.color,
                border: `1px solid ${sourceBadge.border}`,
                fontSize: "var(--text-xs)",
                fontWeight: "var(--font-weight-semibold)",
                letterSpacing: "0.02em",
                whiteSpace: "nowrap",
              }}
              title={`Source: ${sourceBadge.label}`}
            >
              <sourceBadge.Icon className="w-3 h-3" />
              {sourceBadge.label}
            </span>
          )}

          {action.strategicTheme && (
            <span
              className="rounded"
              style={{
                padding: "2px 6px",
                fontSize: "var(--text-xs)",
                fontWeight: "var(--font-weight-semibold)",
                backgroundColor: "var(--muted)",
                color: "var(--muted-foreground)",
              }}
            >
              {action.strategicTheme}
            </span>
          )}

          <div className="flex items-center" style={{ gap: 6, marginLeft: "auto" }}>
            <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
              Effort:{" "}
              <span
                style={{
                  fontWeight: "var(--font-weight-semibold)",
                  color: getEffortColor(action.effort),
                }}
              >
                {action.effort}
              </span>
            </span>
            <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>•</span>
            <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
              Impact:{" "}
              <span
                style={{
                  fontWeight: "var(--font-weight-semibold)",
                  color: getImpactColor(action.impactLevel),
                }}
              >
                {action.impactLevel}
              </span>
            </span>
          </div>
        </div>

        <h3
          style={{
            fontSize: "var(--text-xl)",
            fontWeight: "var(--font-weight-semibold)",
            marginBottom: 4,
            lineHeight: "1.3",
          }}
        >
          {action.title}
        </h3>

        <p
          style={{
            fontSize: "var(--text-sm)",
            color: "var(--muted-foreground)",
            lineHeight: "1.5",
            margin: 0,
          }}
        >
          {action.description}
        </p>
      </div>

      {/* ── Scrollable content ───────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        {action.observation && (
          <Section icon={<Eye className="w-3.5 h-3.5" style={{ color: "var(--spectral-violet)" }} />}
                   iconBg="var(--spectral-violet-10)"
                   label="What We Observed"
                   first>
            <BoxedText>{action.observation}</BoxedText>
          </Section>
        )}

        {action.strategicGoal && (
          <Section icon={<Target className="w-3.5 h-3.5" style={{ color: "var(--alpha-teal)" }} />}
                   iconBg="var(--alpha-teal-10)"
                   label="Strategic Goal">
            <BoxedText>{action.strategicGoal}</BoxedText>
          </Section>
        )}

        {action.artifacts && action.artifacts.length > 0 && (
          <div
            style={{
              marginTop: SECTION_V_GAP,
              paddingLeft: SECTION_H_PAD,
              paddingRight: SECTION_H_PAD,
            }}
          >
            <h4
              style={{
                fontSize: "var(--text-xs)",
                fontWeight: "var(--font-weight-semibold)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "var(--muted-foreground)",
                marginBottom: "8px",
              }}
            >
              Optimization Assets ({action.artifacts.length})
            </h4>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {action.artifacts.map((artifact, artifactIndex) => {
                const isExpanded = expandedArtifacts.has(artifact.id);
                const artifactAnchorId = `artifact-${artifact.id}`;

                const tracked = artifactTracker.getForArtifactSlot(action.id, artifactIndex);
                const isGenerated = !!tracked;
                const generatedStatus = tracked?.status || "";
                const statusColors = isGenerated ? getArtifactStatusColors(generatedStatus) : null;

                return (
                  <div
                    key={artifact.id}
                    className="rounded-lg border-l-4"
                    style={{
                      backgroundColor: "var(--muted)",
                      borderLeftColor: isGenerated
                        ? statusColors?.text || "var(--alpha-teal)"
                        : "var(--infrared-coral)",
                    }}
                  >
                    <div
                      style={{ padding: CONTENT_BOX_PAD, cursor: "pointer" }}
                      className="hover:opacity-80 transition-opacity"
                      onClick={() => toggleArtifact(artifact.id)}
                    >
                      <div className="flex items-center" style={{ gap: 8 }}>
                        <ChevronRight
                          className="w-4 h-4 flex-shrink-0 transition-transform"
                          style={{
                            color: "var(--muted-foreground)",
                            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                          }}
                        />

                        <h6
                          className="flex-1 min-w-0"
                          style={{
                            fontSize: "var(--text-sm)",
                            fontWeight: "var(--font-weight-semibold)",
                          }}
                        >
                          {artifact.title}
                        </h6>

                        <button
                          ref={(el) => {
                            if (el) anchorRefs.current.set(artifactAnchorId, el);
                            else anchorRefs.current.delete(artifactAnchorId);
                          }}
                          onClick={(e) =>
                            handleArtifactTypeClick(e, artifact.type, artifactAnchorId)
                          }
                          className="flex-shrink-0 hover:opacity-70 transition-opacity"
                          style={{
                            padding: "2px 8px",
                            backgroundColor: "var(--background)",
                            fontSize: "var(--text-xs)",
                            color: "var(--muted-foreground)",
                            fontWeight: "var(--font-weight-medium)",
                            border: "1px solid var(--border)",
                            borderRadius: "var(--radius)",
                            cursor: "pointer",
                          }}
                        >
                          {artifact.type}
                        </button>

                        {isGenerated ? (
                          <div className="flex items-center flex-shrink-0" style={{ gap: 6 }}>
                            <span
                              className="inline-flex items-center gap-1 rounded"
                              style={{
                                padding: "2px 6px",
                                backgroundColor: statusColors!.bg,
                                color: statusColors!.text,
                                border: `1px solid ${statusColors!.border}`,
                                fontSize: "var(--text-xs)",
                                fontWeight: "var(--font-weight-medium)",
                              }}
                            >
                              {getArtifactStatusIcon(generatedStatus)}
                              {generatedStatus || "Draft"}
                            </span>
                            {onNavigateToStudio && tracked?.artifactId && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onNavigateToStudio(tracked.artifactId);
                                }}
                                className="flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer inline-flex items-center"
                                style={{
                                  gap: 4,
                                  padding: "3px 8px",
                                  backgroundColor: "var(--signal-blue-10)",
                                  color: "var(--signal-blue)",
                                  fontSize: "var(--text-xs)",
                                  fontWeight: "var(--font-weight-semibold)",
                                  borderRadius: "var(--radius)",
                                  border: "1px solid var(--signal-blue-30)",
                                }}
                              >
                                <ExternalLink className="w-3 h-3" />
                                View
                              </button>
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onGenerateAsset(
                                action.id,
                                action.title,
                                action.strategicTheme || "",
                                artifact.title,
                                artifact.type,
                                artifact.description,
                                artifactIndex,
                                totalArtifacts
                              );
                            }}
                            className="flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer"
                            style={{
                              padding: "4px 10px",
                              backgroundColor: "var(--alpha-teal)",
                              color: "var(--background)",
                              fontSize: "var(--text-xs)",
                              fontWeight: "var(--font-weight-semibold)",
                              borderRadius: "var(--radius)",
                              border: "none",
                            }}
                          >
                            Generate
                          </button>
                        )}
                      </div>

                      {isGenerated && (
                        <div
                          style={{
                            marginTop: 6,
                            marginLeft: 24,
                            padding: "6px 8px",
                            backgroundColor: statusColors!.bg,
                            border: `1px solid ${statusColors!.border}`,
                            borderRadius: 4,
                          }}
                        >
                          <p
                            style={{
                              fontSize: "var(--text-xs)",
                              color: statusColors!.text,
                              lineHeight: "1.4",
                              margin: 0,
                            }}
                          >
                            Generated{" "}
                            {tracked?.generatedAt
                              ? new Date(tracked.generatedAt).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : "—"}{" "}
                            · Status: <strong>{generatedStatus || "Draft"}</strong>
                          </p>
                        </div>
                      )}
                    </div>

                    {isExpanded && (
                      <div
                        style={{
                          padding: `0 12px 10px 12px`,
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        <div
                          style={{
                            padding: "8px 10px",
                            backgroundColor: "var(--background)",
                            borderRadius: 4,
                          }}
                        >
                          <p
                            style={{
                              fontSize: "var(--text-sm)",
                              color: "var(--foreground)",
                              lineHeight: "1.5",
                              margin: 0,
                            }}
                          >
                            {artifact.description}
                          </p>
                        </div>

                        {artifact.expectedImpact && (
                          <div>
                            <MiniLabel>Expected Impact</MiniLabel>
                            <div
                              style={{
                                padding: "8px 10px",
                                backgroundColor: "var(--background)",
                                borderRadius: 4,
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "var(--text-sm)",
                                  color: "var(--foreground)",
                                  lineHeight: "1.5",
                                  margin: 0,
                                }}
                              >
                                {artifact.expectedImpact}
                              </p>
                            </div>
                          </div>
                        )}

                        {artifact.deployment && artifact.deployment.length > 0 && (
                          <div>
                            <MiniLabel>Deployment</MiniLabel>
                            <div className="flex flex-wrap" style={{ gap: 6 }}>
                              {artifact.deployment.map((dep, idx) => {
                                const depAnchorId = `deployment-${artifact.id}-${idx}`;
                                return (
                                  <button
                                    key={idx}
                                    ref={(el) => {
                                      if (el) anchorRefs.current.set(depAnchorId, el);
                                      else anchorRefs.current.delete(depAnchorId);
                                    }}
                                    onClick={(e) => handleDeploymentClick(e, dep, depAnchorId)}
                                    className="hover:opacity-70 transition-opacity"
                                    style={{
                                      padding: "3px 8px",
                                      backgroundColor: "var(--background)",
                                      border: `1px solid ${getDeploymentColor(dep)}`,
                                      color: getDeploymentColor(dep),
                                      fontSize: "var(--text-xs)",
                                      fontWeight: "var(--font-weight-medium)",
                                      borderRadius: "var(--radius)",
                                      cursor: "pointer",
                                    }}
                                  >
                                    {dep}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        <div className="flex justify-end">
                          <InsightActionButton
                            title={`${action.title} - ${artifact.title}`}
                            content={[artifact.description, artifact.expectedImpact].filter(
                              isNonEmptyString
                            )}
                            context={{
                              module: "cognition",
                              stage: "fix-actions",
                              stageName: action.title,
                              productName: action.category,
                              timestamp: new Date(),
                              accentColor: insightAccent,
                            }}
                            size="sm"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {action.reasoningInfluence && (
          <Section icon={<Lightbulb className="w-3.5 h-3.5" style={{ color: "var(--warning-text)" }} />}
                   iconBg="var(--warning-bg)"
                   label="Why This Influences AI Reasoning">
            <BoxedText>{action.reasoningInfluence}</BoxedText>
          </Section>
        )}

        {action.expectedOutcome && (
          <Section icon={<Sparkles className="w-3.5 h-3.5" style={{ color: "var(--alpha-teal)" }} />}
                   iconBg="var(--alpha-teal-10)"
                   label="Expected Outcome"
                   last>
            <BoxedText>{action.expectedOutcome}</BoxedText>
          </Section>
        )}

        <div
          className="border-t"
          style={{
            borderColor: "var(--border)",
            backgroundColor: "var(--background)",
            padding: "10px 14px",
          }}
        >
          <Button
            variant="outline"
            onClick={() => onExplainWithAI(action.title)}
            className="w-full"
            style={{
              backgroundColor: "transparent",
              borderColor: "var(--spectral-violet)",
              color: "var(--spectral-violet)",
              fontWeight: "var(--font-weight-semibold)",
            }}
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Explain this with AI
          </Button>
        </div>
      </div>

      {activePopover && popoverContent && popoverAnchorEl && (
        <DefinitionPopover
          isOpen={true}
          onClose={() => setActivePopover(null)}
          title={popoverContent.title}
          definition={popoverContent.definition}
          items={popoverContent.items}
          proTip={popoverContent.proTip}
          anchorRef={{ current: popoverAnchorEl }}
        />
      )}
    </div>
  );
}

// ── Small presentational helpers (keep the main JSX skimmable) ──

function Section({
  icon,
  iconBg,
  label,
  children,
  first = false,
  last = false,
}: {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  children: React.ReactNode;
  first?: boolean;
  last?: boolean;
}) {
  return (
    <div
      style={{
        marginTop: first ? "var(--gap-md)" : SECTION_V_GAP,
        marginBottom: last ? "var(--gap-md)" : 0,
        paddingLeft: SECTION_H_PAD,
        paddingRight: SECTION_H_PAD,
      }}
    >
      <div
        className="flex items-center"
        style={{ gap: 8, marginBottom: SECTION_HEADING_MB }}
      >
        <div
          className="rounded flex items-center justify-center flex-shrink-0"
          style={{ width: 22, height: 22, backgroundColor: iconBg }}
        >
          {icon}
        </div>
        <h4
          style={{
            fontSize: "var(--text-xs)",
            fontWeight: "var(--font-weight-semibold)",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "var(--muted-foreground)",
            margin: 0,
          }}
        >
          {label}
        </h4>
      </div>
      {children}
    </div>
  );
}

function BoxedText({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="rounded-lg"
      style={{ padding: CONTENT_BOX_PAD, backgroundColor: "var(--muted)" }}
    >
      <p
        style={{
          fontSize: "var(--text-sm)",
          color: "var(--foreground)",
          lineHeight: "1.5",
          margin: 0,
        }}
      >
        {children}
      </p>
    </div>
  );
}

function MiniLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: "var(--text-xs)",
        fontWeight: "var(--font-weight-semibold)",
        color: "var(--muted-foreground)",
        marginBottom: 4,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
      }}
    >
      {children}
    </div>
  );
}